import * as Sentry from "@sentry/browser";
import axios from "axios";
import { env } from "@infra/config/env";
import { Notifications } from "@infra/notifications";
import { USER_NOT_FOUND, USER_NOT_LINKED } from "@infra/api/common";
import { LogData } from "@rogoag/shared-ts";
import { logger } from "@infra/logger";

export namespace Oops {
	export function handleError(
		error: any,
		source: string = "",
		email: string = "",
		shouldCreateUniqueIssue: boolean = false,
		context?: Record<string, unknown>,
	) {
		// If the axios interceptor already handled this error (toasted + Sentry), skip.
		if (error?._interceptorHandled) return;

		if (env.debug) console.error("OOPS", error);

		if (email) {
			Sentry.setUser({ email });
		}

		// Attach structured context so Sentry events carry the surrounding state
		// (selected branch/deal/growers, etc.) without needing to hand-correlate.
		if (context && Object.keys(context).length > 0) {
			Sentry.setContext("operation", context);
		}

		const message = getErrorMessage(error);
		const logData: LogData = {
			function: source,
			email: email,
			...(context ? { context } : {}),
		};
		switch (message) {
			// Known business errors — toast specific messages
			case "User does not exist":
				Notifications.error("User does not yet exist in our database. Contact support@rogoag.com");
				logger.warning(message, logData);
				break;
			case "User is not registered":
				Notifications.error("User is not registered. Please register the user or contact software@rogoag.com");
				logger.warning(message, logData);
				break;
			case "User is already registered":
			case "User registration has already been submitted":
			case "Error updating password":
			case "User is undefined":
				logger.warning(message, logData);
				break;
			case "Incorrect username or password":
				Notifications.error(message);
				logger.warning(message, logData);
				break;
			case USER_NOT_FOUND:
			case USER_NOT_LINKED:
				Notifications.error(message);
				logger.warning(message, logData);
				break;

			default:
				if (isUserFacing422(error)) {
					// A 422 with a string detail is a deliberate, user-facing rejection
					// (e.g. "Job '#2 North': the boundary file contains a feature with
					// empty geometry") — show it instead of the generic message.
					// FastAPI's automatic validation 422s carry a list detail and still
					// fall through to the generic toast.
					Notifications.error(message);
				} else {
					Notifications.error("An error occurred. Contact software@rogoag.com");
				}
				logErrorToSentry(error, source, shouldCreateUniqueIssue);
				logger.error(message, logData, error);
				break;
		}
	}

	export function logErrorToSentry(
		error: Error | any,
		errorSource: string = "",
		shouldCreateUniqueIssue: boolean = false,
	) {
		// var e = castErrorToSpecificType(error, errorSource);

		if (shouldCreateUniqueIssue) {
			Sentry.withScope(function (scope) {
				scope.setFingerprint(["custom-" + Date.now().toString()]); // forces Sentry to log each event as a separate issue, rather then grouping them together
				if (errorSource) scope.setTransactionName(errorSource);
				scope.captureException(error, { mechanism: { handled: true } });
			});
		} else {
			if (errorSource) Sentry.getCurrentScope().setTransactionName(errorSource);
			Sentry.captureException(error, { mechanism: { handled: true } });
		}
	}

	/** True for a 422 whose detail is a plain string — a deliberate, user-facing
	 *  rejection written by our API (FastAPI's automatic validation 422s carry a
	 *  list-of-dicts detail instead and must stay on the generic toast). */
	function isUserFacing422(error: any): boolean {
		return (
			axios.isAxiosError(error) && error.response?.status === 422 && typeof error.response.data?.detail === "string"
		);
	}

	export function getErrorMessage(error: any) {
		let message = "";
		if (axios.isAxiosError(error) && error.response) {
			message = error.response.data.detail;
			if (env.debug) console.error("Axios Message: ", message);
		} else {
			message = error.message;
		}
		return message;
	}

	export function getErrorStack(error: Error) {
		let stack = "";
		if (axios.isAxiosError(error) && error.response) {
			stack = error.stack ?? "";
		} else {
			stack = error.stack ?? "";
		}
		return stack;
	}
}
